import classnames from 'classnames';
import {
  useCallback,
  useEffect,
  useState,
  type FunctionComponent,
} from 'react';
import { useSelector } from 'react-redux';

import { pageNameSelector } from '@/domains/core/page/page.selectors';
import { CURRENCY } from '@/domains/core/settings/constants';
import { Gtm } from '@/domains/core/tracking/utils/Gtm';
import { useFacet } from '@/domains/productDiscovery/Listings/facets/hooks/useFacet';
import { useFacetStats } from '@/domains/productDiscovery/Listings/facets/hooks/useFacetsStats';
import { useCategoryFamilyId } from '@/domains/productDiscovery/Listings/pages/Category/hooks/useCategoryFamilyId';
import { useCategoryId } from '@/domains/productDiscovery/Listings/pages/Category/hooks/useCategoryId';
import { useCategoryLevel } from '@/domains/productDiscovery/Listings/pages/Category/hooks/useCategoryLevel';

import { FilterBlock } from '../FilterBlock';
import { RangeSelect } from '../RangeSelect/RangeSelect';
import { CURRENCY_SYMBOLS } from './constants';

import styles from './RangeFilter.module.scss';

interface Props {
  title: string;
  facetName: string;
  className?: string;
  position?: number;
  trackingId?: string;
  filterCount?: number;
  type?: string;
}

const DEFAULT_MIN = 0;
const DEFAULT_MAX = 50000;

export const RangeFilter: FunctionComponent<Props> = ({
  className,
  facetName,
  title,
  position,
  trackingId,
  filterCount,
  type,
}) => {
  const facetStats = useFacetStats(facetName);
  const { facet, refineFacetValue } = useFacet<'range'>(facetName);
  const pageName = useSelector(pageNameSelector);
  const categoryId = useCategoryId();
  const categoryLevel = useCategoryLevel();
  const categoryFamilyId = useCategoryFamilyId();

  const avg = facetStats?.avg;
  const minValue = facet?.rangeValue?.min || facetStats?.min || DEFAULT_MIN;
  const maxValue = facet?.rangeValue?.max || facetStats?.max || DEFAULT_MAX;
  const [initial, setIntial] = useState<{
    min: number;
    max: number;
    average?: number;
  }>({
    min: minValue,
    max: maxValue,
    average: avg,
  });
  const [values, setValues] = useState<{ min: number; max: number }>({
    min: minValue,
    max: maxValue,
  });

  const getUnit = (filterType: string) => {
    switch (filterType) {
      case 'price':
        return CURRENCY_SYMBOLS[CURRENCY];
      default:
        return undefined;
    }
  };

  useEffect(() => {
    setIntial({
      min: minValue,
      max: maxValue,
      average: avg,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageName]);

  useEffect(() => {
    if (facetStats !== undefined) {
      setValues({
        min: facet?.rangeValue?.min || facetStats?.min,
        max: facet?.rangeValue?.max || facetStats?.max,
      });
    }
  }, [facet?.rangeValue?.max, facet?.rangeValue?.min, facetStats]);

  const onRefine = useCallback(
    async (_: unknown, nextValues: { min: number; max: number }) => {
      refineFacetValue({ min: nextValues.min, max: nextValues.max });

      const { generateFiltersTracking } = await import(
        '@/domains/productDiscovery/Listings/components/Filters/tracking/generateFiltersTracking'
      );
      Gtm.push(
        generateFiltersTracking({
          e_label: `[${nextValues.min}, ${nextValues.max}]`,
          filter_id: trackingId,
          filter_name: title,
          filter_value: [nextValues.min, nextValues.max],
          filter_value_min: nextValues.min,
          filter_value_max: nextValues.max,
          filter_position: position,
          filter_count: filterCount,
          category_id: categoryId,
          category_level: categoryLevel,
          family_id: categoryFamilyId,
        }),
      );
    },
    [
      refineFacetValue,
      trackingId,
      title,
      position,
      filterCount,
      categoryId,
      categoryLevel,
      categoryFamilyId,
    ],
  );

  const rootClassnames = classnames(styles.root, className);
  const rangeLowerThanOne = initial.max - initial.min < 1;

  return facetStats ? (
    <FilterBlock
      className={rootClassnames}
      id={facetName}
      title={title}
      disabled={!facetStats || rangeLowerThanOne}
    >
      <>
        <RangeSelect
          className={styles.selectRange}
          id={facetName}
          min={initial.min}
          max={initial.max}
          values={values}
          onChange={onRefine}
          unit={type && getUnit(type)}
        />
      </>
    </FilterBlock>
  ) : null;
};

RangeFilter.displayName = 'RangeFilter';
